import { useState, useCallback } from 'react';
import { Plus, Calendar, User, Building2, Mail, Bell, Pin } from 'lucide-react';
import { PageShell } from '@/components/common/PageShell';
import { WorkspaceToolbar } from '@/components/common/WorkspaceToolbar';
import { Badge, Button, Card, ConfirmDialog } from '@/components/ui';
import { SectionLoader } from '@/components/common/SectionLoader';
import { PermissionGate } from '@/components/common/PermissionGate';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useAnnouncements,
  usePublishAnnouncement,
  useArchiveAnnouncement,
  useDeleteAnnouncement,
} from '../hooks/useAnnouncements';
import { AnnouncementForm } from './AnnouncementForm';
import { cn } from '@/lib/cn';
import type { Announcement } from '@/types/announcement.types';

type ViewTab = 'manage' | 'create';

const STATUS_VARIANT: Record<string, 'warning' | 'success' | 'neutral'> = {
  DRAFT: 'warning',
  PUBLISHED: 'success',
  ARCHIVED: 'neutral',
};

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  PUBLISHED: 'Published',
  ARCHIVED: 'Archived',
};

export function AnnouncementsPage(): React.ReactElement {
  const { can } = usePermissions();
  const canCreate = can('announcements', 'create');
  const canPublish = can('announcements', 'publish');
  const canArchive = can('announcements', 'archive');
  const canDelete = can('announcements', 'delete');
  const canManageEditorial =
    canCreate ||
    canPublish ||
    canArchive ||
    canDelete ||
    can('announcements', 'update') ||
    can('announcements', 'manage');

  const [activeTab, setActiveTab] = useState<ViewTab>('manage');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [confirmAction, setConfirmAction] = useState<{
    type: string;
    id: string;
    title: string;
  } | null>(null);

  const limit = 10;
  const {
    data: listData,
    isLoading,
    error,
  } = useAnnouncements({
    page,
    limit,
    status: canManageEditorial ? (statusFilter as any) : undefined,
  });
  const publishMutation = usePublishAnnouncement();
  const archiveMutation = useArchiveAnnouncement();
  const deleteMutation = useDeleteAnnouncement();

  const announcements = listData?.data ?? [];
  const total = listData?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  const handleConfirmAction = useCallback(() => {
    if (!confirmAction) return;
    switch (confirmAction.type) {
      case 'publish':
        publishMutation.mutate(confirmAction.id);
        break;
      case 'archive':
        archiveMutation.mutate(confirmAction.id);
        break;
      case 'delete':
        deleteMutation.mutate(confirmAction.id);
        break;
    }
    setConfirmAction(null);
  }, [confirmAction, publishMutation, archiveMutation, deleteMutation]);

  const handleFormSuccess = useCallback(() => {
    setActiveTab('manage');
    setEditingAnnouncement(null);
  }, []);

  const handleEditClick = useCallback((announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setActiveTab('create');
  }, []);

  const getAuthorName = (authorId: Announcement['authorId']) => {
    if (typeof authorId === 'string') return 'Unknown';
    return `${authorId.firstName} ${authorId.lastName}`;
  };

  const getTargetLabel = (a: Announcement) => {
    if (a.audienceType === 'ORGANIZATION') return 'All Organization';
    if (Array.isArray(a.targetCompanyIds) && a.targetCompanyIds.length > 0) {
      const first = a.targetCompanyIds[0];
      const name = typeof first === 'string' ? first : first.name;
      const extra = a.targetCompanyIds.length > 1 ? ` +${a.targetCompanyIds.length - 1}` : '';
      return `${name}${extra}`;
    }
    return 'Specific Companies';
  };

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const statusFilters = [
    { value: undefined, label: 'All' },
    { value: 'DRAFT', label: 'Draft' },
    { value: 'PUBLISHED', label: 'Published' },
    { value: 'ARCHIVED', label: 'Archived' },
  ] as const;

  return (
    <PageShell
      breadcrumbItems={[{ label: 'Organization', to: '/dashboard' }, { label: 'Announcements' }]}
      icon={Bell}
      title="Announcements"
      description={
        activeTab === 'manage'
          ? 'Create and manage announcements for your organization.'
          : editingAnnouncement
            ? `Editing: ${editingAnnouncement.title}`
            : 'Create a new announcement.'
      }
      className="gap-0"
      hideHeader
    >
      <WorkspaceToolbar
        actions={
          <PermissionGate module="announcements" action="create">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => {
                setActiveTab('create');
                setEditingAnnouncement(null);
              }}
              className="shadow-sm"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span className="ml-1.5">New announcement</span>
            </Button>
          </PermissionGate>
        }
      />

      <div className="space-y-4">
        {/* Tabs */}
        <div
          className="border-b border-gray-200 dark:border-gray-700"
          role="tablist"
          aria-label="Announcement views"
        >
          <nav className="-mb-px flex gap-1">
            <button
              type="button"
              role="tab"
              id="manage-tab"
              aria-selected={activeTab === 'manage'}
              tabIndex={activeTab === 'manage' ? 0 : -1}
              onClick={() => {
                setActiveTab('manage');
                setEditingAnnouncement(null);
              }}
              className={cn(
                'border-b-2 px-4 py-3 text-sm font-medium',
                activeTab === 'manage'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
              )}
            >
              All Announcements
            </button>
            {canCreate && (
              <button
                type="button"
                role="tab"
                id="create-tab"
                aria-selected={activeTab === 'create'}
                tabIndex={activeTab === 'create' ? 0 : -1}
                onClick={() => {
                  setActiveTab('create');
                  setEditingAnnouncement(null);
                }}
                className={cn(
                  'border-b-2 px-4 py-3 text-sm font-medium',
                  activeTab === 'create'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200'
                )}
              >
                {editingAnnouncement ? 'Edit Announcement' : 'Create'}
              </button>
            )}
          </nav>
        </div>

        {/* Manage Tab */}
        {activeTab === 'manage' && (
          <section role="tabpanel" id="manage-panel" aria-labelledby="manage-tab" tabIndex={0}>
            <Card
              header={
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    All announcements
                  </span>
                  {canManageEditorial && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      {statusFilters.map((s) => (
                        <button
                          key={s.label}
                          type="button"
                          onClick={() => {
                            setStatusFilter(s.value);
                            setPage(1);
                          }}
                          className={cn(
                            'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                            (s.value === undefined && !statusFilter) || statusFilter === s.value
                              ? 'bg-primary/10 text-primary dark:bg-primary/20'
                              : 'text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                          )}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              }
              contentClassName="p-0"
              footer={
                !isLoading && !error && total > 0 && totalPages > 1 ? (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        disabled={page >= totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                ) : undefined
              }
            >
              {/* Loading */}
              {isLoading && (
                <SectionLoader variant="cards" label="Loading announcements" />
              )}

              {/* Error */}
              {error && (
                <div className="px-4 py-12 text-center">
                  <p className="text-sm text-red-500">
                    Error loading announcements: {(error as Error).message}
                  </p>
                </div>
              )}

              {/* Empty */}
              {!isLoading && !error && announcements.length === 0 && (
                <div className="px-4 py-12 text-center">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No announcements found.
                  </p>
                  {canCreate && (
                    <Button
                      type="button"
                      variant="secondary"
                      className="mt-3"
                      onClick={() => {
                        setActiveTab('create');
                        setEditingAnnouncement(null);
                      }}
                    >
                      Create your first announcement
                    </Button>
                  )}
                </div>
              )}

              {/* List */}
              {!isLoading && !error && announcements.length > 0 && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {announcements.map((a) => (
                    <div
                      key={a._id}
                      className="group flex flex-col gap-3 px-4 py-4 transition-colors hover:bg-gray-50/50 dark:hover:bg-gray-750/50 sm:px-6 sm:flex-row sm:items-start sm:justify-between"
                    >
                      <div className="min-w-0 flex-1">
                        {/* Title row */}
                        <div className="flex flex-wrap items-center gap-2">
                          {a.isPinned && (
                            <Pin className="h-3.5 w-3.5 text-primary" aria-label="Pinned" />
                          )}
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {a.title}
                          </h3>
                          <Badge variant={STATUS_VARIANT[a.status] || 'neutral'}>
                            {STATUS_LABELS[a.status] || a.status}
                          </Badge>
                        </div>

                        {/* Content preview */}
                        <p className="mt-1 line-clamp-2 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                          {a.content}
                        </p>

                        {/* Metadata */}
                        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-500">
                          <span className="inline-flex items-center gap-1">
                            <User className="h-3 w-3" aria-hidden />
                            {getAuthorName(a.authorId)}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Building2 className="h-3 w-3" aria-hidden />
                            {getTargetLabel(a)}
                          </span>
                          {a.sendEmail && (
                            <span className="inline-flex items-center gap-1">
                              <Mail className="h-3 w-3" aria-hidden />
                              Email
                            </span>
                          )}
                          {a.sendNotification && (
                            <span className="inline-flex items-center gap-1">
                              <Bell className="h-3 w-3" aria-hidden />
                              Notification
                            </span>
                          )}
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" aria-hidden />
                            {formatDate(a.createdAt)}
                          </span>
                          {a.publishedAt && (
                            <span className="text-green-600 dark:text-green-400">
                              Published {formatDate(a.publishedAt)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex shrink-0 items-center gap-2 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100">
                        {a.status === 'DRAFT' && canCreate && (
                          <Button variant="ghost" onClick={() => handleEditClick(a)}>
                            Edit
                          </Button>
                        )}
                        {a.status === 'DRAFT' && canPublish && (
                          <Button
                            variant="primary"
                            onClick={() =>
                              setConfirmAction({ type: 'publish', id: a._id, title: a.title })
                            }
                          >
                            Publish
                          </Button>
                        )}
                        {a.status === 'PUBLISHED' && canArchive && (
                          <Button
                            variant="secondary"
                            onClick={() =>
                              setConfirmAction({ type: 'archive', id: a._id, title: a.title })
                            }
                          >
                            Archive
                          </Button>
                        )}
                        {a.status === 'DRAFT' && canDelete && (
                          <Button
                            variant="danger"
                            onClick={() =>
                              setConfirmAction({ type: 'delete', id: a._id, title: a.title })
                            }
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </section>
        )}

        {/* Create/Edit Tab */}
        {activeTab === 'create' && canCreate && (
          <section role="tabpanel" id="create-panel" aria-labelledby="create-tab" tabIndex={0}>
            <Card
              header={
                <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingAnnouncement ? 'Edit announcement' : 'New announcement'}
                </span>
              }
            >
              <AnnouncementForm
                announcement={editingAnnouncement}
                onSuccess={handleFormSuccess}
                onCancel={() => {
                  setActiveTab('manage');
                  setEditingAnnouncement(null);
                }}
              />
            </Card>
          </section>
        )}

        {/* Confirm Dialog */}
      </div>

      <ConfirmDialog
        open={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
        title={
          confirmAction?.type === 'publish'
            ? 'Publish Announcement'
            : confirmAction?.type === 'archive'
              ? 'Archive Announcement'
              : 'Delete Announcement'
        }
        message={
          confirmAction?.type === 'publish'
            ? `Publish "${confirmAction.title}"? This will send notifications to eligible users based on your delivery settings.`
            : confirmAction?.type === 'archive'
              ? `Archive "${confirmAction?.title}"? It will no longer appear in the banner feed.`
              : `Delete "${confirmAction?.title}"? This action cannot be undone.`
        }
        confirmLabel={
          confirmAction?.type === 'delete'
            ? 'Delete'
            : confirmAction?.type === 'publish'
              ? 'Publish'
              : 'Archive'
        }
        variant={confirmAction?.type === 'delete' ? 'danger' : 'default'}
        confirmLoading={
          publishMutation.isPending || archiveMutation.isPending || deleteMutation.isPending
        }
      />
    </PageShell>
  );
}

AnnouncementsPage.displayName = 'AnnouncementsPage';
